import { useRef, useState } from 'react';
import { ActionIcon, Tooltip, Modal, Select } from '@mantine/core';
import { IconMessage } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { MemberModelAPI } from '@bobex/react-core/domain/MemberModel/MemberModelAPI';
import { CRMCommentModalContainer } from '@/pages/InvoiceList/components/CRMCommentModalContainer';

/**
 * Global header "add comment" button — picks a member, then opens the CRM comment modal for them.
 * Ports the legacy top-menu bo_menu_addusercomment (member autocomplete + add CRM comment).
 */
export default function GlobalCrmButton() {
  const { t } = useTranslation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [crmOpen, setCrmOpen] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleSearch = (value: string) => {
    if (debounce.current) clearTimeout(debounce.current);
    if (!value || value.trim().length < 2) {
      setOptions([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      try {
        const page = await MemberModelAPI.memberAutocomplete(value.trim());
        setOptions(page.content.map((m) => ({ value: String(m.id), label: `${m.companyName} (#${m.id})` })));
      } catch {
        setOptions([]);
      }
    }, 300);
  };

  const handleSelect = (val: string | null) => {
    if (!val) return;
    setSelectedUserId(Number(val));
    setSearchOpen(false);
    setCrmOpen(true);
  };

  return (
    <>
      <Tooltip label={t('Add comment')}>
        <ActionIcon variant="default" size="lg" aria-label="Add comment" onClick={() => setSearchOpen(true)}>
          <IconMessage stroke={1.5} />
        </ActionIcon>
      </Tooltip>

      <Modal opened={searchOpen} onClose={() => setSearchOpen(false)} title={t('Add comment')} size="md">
        <Select
          label={t('Member')}
          placeholder={t('Search by company name')}
          data={options}
          searchable
          clearable
          onSearchChange={handleSearch}
          onChange={handleSelect}
          nothingFoundMessage={t('No members found')}
          // Server already filtered — don't client-filter the results away.
          filter={({ options }) => options}
        />
      </Modal>

      <CRMCommentModalContainer
        opened={crmOpen}
        userId={selectedUserId}
        onClose={() => setCrmOpen(false)}
        onSuccess={() => setCrmOpen(false)}
      />
    </>
  );
}
